import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from 'utils/api';
import { logError } from 'utils/datadog';
import { saveGuestCv } from './api';
import { GuestSaveRequest } from './api-types';
import { isTerminalGrantError } from './grantErrors';
import { GuestSession } from './GuestSessionContext';

/** Coalesce a burst of autosaves (e.g. rapid Next clicks) into one network write. */
const DEBOUNCE_MS = 400;

interface GuestAutosave {
  /** Fire-and-forget, debounced autosave. Never rejects. */
  scheduleSave: (request: GuestSaveRequest) => void;
  /** Awaited, latest-wins save for the submit gate — cancels any pending/in-flight save. May reject. */
  flushSave: (request: GuestSaveRequest) => Promise<void>;
  /** Timestamp (ms) of the last save the server accepted, or null if none yet. */
  lastSavedAt: number | null;
  /**
   * True when the last attempt failed for a non-terminal reason (validation, network, 5xx).
   *
   * pro-core runs `CreateCvHttpValidator` on EVERY guest autosave, and it rejects the whole
   * payload for a single bad entry (e.g. an experience row whose end date precedes its
   * start). Those failures used to be logged and forgotten, so the talent kept typing into
   * a form that had stopped persisting — this is what makes that visible.
   */
  saveFailed: boolean;
}

/**
 * Autosave manager for the guest CV with two guarantees the wizard needs:
 *  - **Single-flight:** every new save aborts the previous in-flight request, so a slow
 *    older write can never land after (and overwrite) a newer one.
 *  - **Debounce:** rapid saves collapse into one network call.
 *
 * `scheduleSave` is fire-and-forget and swallows errors (validation noise is ignored).
 * On a 401 (lapsed 60-min session) it transparently re-redeems via {@link recoverSession}
 * and retries once with the fresh token; only a genuinely dead grant (recoverSession →
 * null, which has already routed to the terminal screen) ends the flow. A terminal grant
 * error on the save itself (already-submitted / expired / invalid — e.g. submitted from a
 * second tab) routes to the matching terminal screen via `routeGrantError` instead of
 * being swallowed, so the guest stops editing a dead session.
 * `flushSave` is the submit gate: it cancels anything pending, performs one awaited
 * save of the latest values (with the same recover-and-retry), and propagates failures.
 */
export const useGuestAutosave = (
  sessionToken: string,
  recoverSession: () => Promise<string | null>,
  routeGrantError: GuestSession['routeGrantError']
): GuestAutosave => {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const controllerRef = useRef<AbortController | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  const markSaved = useCallback(() => {
    setLastSavedAt(Date.now());
    setSaveFailed(false);
  }, []);

  const startRequest = useCallback(() => {
    // single-flight: tear down the previous request before starting a new one
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    return controller;
  }, []);

  const runSave = useCallback(
    async (request: GuestSaveRequest) => {
      const controller = startRequest();
      try {
        await saveGuestCv(sessionToken, request, controller.signal);
        markSaved();
      } catch (error) {
        if (controller.signal.aborted) {
          return; // superseded by a newer save — expected, ignore
        }
        if (
          error instanceof ApiError &&
          error.code === 'error.guest.session-expired'
        ) {
          // Lapsed session: re-redeem the still-valid link and retry once with the fresh
          // token. recoverSession returns null only when the grant is truly dead (it has
          // already routed to the terminal screen), so we simply stop.
          const freshToken = await recoverSession();
          if (!freshToken || controller.signal.aborted) {
            return;
          }
          try {
            await saveGuestCv(freshToken, request, controller.signal);
            markSaved();
          } catch (retryError) {
            if (controller.signal.aborted) {
              return;
            }
            if (isTerminalGrantError(retryError)) {
              routeGrantError(retryError, 'autosave');
              return;
            }
            setSaveFailed(true);
            logError('Guest CV autosave failed after recovery', retryError);
          }
          return;
        }
        if (isTerminalGrantError(error)) {
          // Dead grant (e.g. submitted from another tab): stop autosaving and swap to the
          // terminal screen — retrying would just hammer the backend with 400s.
          routeGrantError(error, 'autosave');
          return;
        }
        // Still swallowed as far as the flow is concerned — a failed autosave must never
        // block navigation — but no longer silent: the wizard shows "couldn't save" so the
        // talent doesn't keep typing into a form that stopped persisting.
        setSaveFailed(true);
        logError('Guest CV autosave failed', error);
      }
    },
    [sessionToken, recoverSession, routeGrantError, startRequest, markSaved]
  );

  const scheduleSave = useCallback(
    (request: GuestSaveRequest) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        void runSave(request);
      }, DEBOUNCE_MS);
    },
    [runSave]
  );

  const flushSave = useCallback(
    async (request: GuestSaveRequest) => {
      // Submit gate: cancel any pending debounce + abort the in-flight autosave, then run
      // our OWN save with no shared controller — so a concurrent scheduleSave can't abort
      // this one (which would surface a spurious AbortError) and it always completes.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      controllerRef.current?.abort();
      try {
        await saveGuestCv(sessionToken, request);
        markSaved();
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.code === 'error.guest.session-expired'
        ) {
          const freshToken = await recoverSession();
          if (freshToken) {
            await saveGuestCv(freshToken, request);
            markSaved();
            return;
          }
        }
        setSaveFailed(true);
        throw error; // dead grant (terminal shown) or other error — let submit handle it
      }
    },
    [sessionToken, recoverSession, markSaved]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      controllerRef.current?.abort();
    },
    []
  );

  return { scheduleSave, flushSave, lastSavedAt, saveFailed };
};
